import { useEffect, useRef, useState } from "react";
import {
  ArrowUpRight,
  ChevronDown,
  GripVertical,
  Menu,
  Palette,
  RotateCcw,
  X,
} from "lucide-react";
import {
  AddMenu,
  ConfirmReset,
  ContextPanel,
  EditorAddButton,
  EditorRail,
  FloatingToolbar,
  ModeSwitch,
  Onboarding,
  ThemePanel,
} from "./components/Editor";
import { SECTION_REGISTRY } from "./components/Sections";
import { animationStyle, cn, sanitizeUrl, validateSiteShape } from "./lib";
import { useEditor } from "./store";
import { useReveal } from "./hooks";
import type { SiteConfig } from "./types";

function Header() {
  const { site, editing, updateSite } = useEditor();
  const [menu, setMenu] = useState(false);
  return (
    <header className="site-header">
      <a className="logo" href="#top" aria-label="ZhStudio home">
        Z<span>h</span>
        <i>studio</i>
      </a>
      <nav className={menu ? "open" : ""}>
        {site.navigation.map((item) => (
          <a
            key={item.id}
            href={sanitizeUrl(item.href)}
            contentEditable={editing}
            suppressContentEditableWarning
            onBlur={(e) =>
              updateSite((target) => {
                const nav = target.navigation.find((n) => n.id === item.id);
                if (nav)
                  nav.label = e.currentTarget.textContent?.slice(0, 80) ?? "";
              })
            }
          >
            {item.label}
          </a>
        ))}
      </nav>
      <ModeSwitch />
      <button
        className="mobile-menu"
        onClick={() => setMenu(!menu)}
        aria-label="Toggle menu"
      >
        {menu ? <X /> : <Menu />}
      </button>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer id="contact">
      <div className="container footer-grid">
        <div>
          <a className="logo light" href="#top">
            Z<span>h</span>
            <i>studio</i>
          </a>
          <p>Custom websites with a beautifully simple editor.</p>
        </div>
        <div>
          <small>Start something</small>
          <a href="mailto:hello@zhstudio.ch">
            hello@zhstudio.ch <ArrowUpRight />
          </a>
          <span>Zürich, Switzerland</span>
        </div>
        <div>
          <small>Elsewhere</small>
          <a href="#top">Instagram ↗</a>
          <a href="#top">LinkedIn ↗</a>
        </div>
      </div>
      <div className="container footer-bottom">
        <span>© {year} ZhStudio</span>
        <span>Made with care. Delivered by Cloudflare.</span>
        <a href="#top">Back to top ↑</a>
      </div>
    </footer>
  );
}

async function loadSession(): Promise<SiteConfig | null> {
  try {
    const response = await fetch("/api/session", {
      headers: { Accept: "application/json" },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { site?: unknown };
    return validateSiteShape(data.site) ? data.site : null;
  } catch {
    return null;
  }
}

export default function App() {
  const {
    site,
    editing,
    preview,
    selection,
    select,
    onboardingSeen,
    reset,
    setSaveState,
    showToast,
    toast,
    hydrateRemote,
  } = useEditor();
  const [themeOpen, setThemeOpen] = useState(false),
    [addOpen, setAddOpen] = useState(false),
    [resetOpen, setResetOpen] = useState(false);
  const revealRef = useReveal();
  const hydrated = useRef(false);
  const saveTimer = useRef<number | null>(null);
  useEffect(() => {
    loadSession().then((remote) => {
      if (remote) hydrateRemote(remote);
      hydrated.current = true;
    });
  }, [hydrateRemote]);
  useEffect(() => {
    if (!hydrated.current) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    setSaveState("saving");
    saveTimer.current = window.setTimeout(async () => {
      try {
        const response = await fetch("/api/session", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ site }),
        });
        setSaveState(response.ok ? "saved" : "offline");
      } catch {
        setSaveState("offline");
      }
    }, 900);
    return () => {
      if (saveTimer.current) window.clearTimeout(saveTimer.current);
    };
  }, [site, setSaveState]);
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => showToast(null), 2800);
    return () => window.clearTimeout(timer);
  }, [toast, showToast]);
  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (mod && event.key.toLowerCase() === "z") {
        event.preventDefault();
        event.shiftKey
          ? useEditor.getState().redo()
          : useEditor.getState().undo();
      }
      if (event.key === "Escape") {
        select(null);
        setAddOpen(false);
        setThemeOpen(false);
      }
      if (mod && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setAddOpen(true);
      }
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, [select]);
  const theme = site.theme;
  return (
    <div
      id="top"
      ref={revealRef}
      className={cn("app", editing && "editing", preview && "preview")}
      style={
        {
          "--bg": theme.background,
          "--surface": theme.surface,
          "--text": theme.text,
          "--muted": theme.muted,
          "--primary": theme.primary,
          "--secondary": theme.secondary,
          "--accent": theme.accent,
          "--button-text": theme.buttonText,
          "--border": theme.border,
          "--radius": `${theme.radius}px`,
        } as React.CSSProperties
      }
    >
      <Header />
      {editing && !preview && <EditorRail onTheme={() => setThemeOpen(true)} />}
      <main className="site-main">
        {site.sections.map((section) => {
          const Component = SECTION_REGISTRY[section.type];
          if (!Component) return null;
          const selected =
            selection?.sectionId === section.id && selection.kind === "section";
          return (
            <section
              id={section.id}
              key={section.id}
              data-reveal
              className={cn(
                "site-section",
                `section-${section.type}`,
                `anim-${section.animation.name}`,
                selected && "cms-selected",
              )}
              style={{
                ...animationStyle(section.animation),
                background: section.style.background,
                color: section.style.color,
                paddingBlock: section.style.padding,
                borderRadius: section.style.radius,
                boxShadow: section.style.shadow
                  ? `0 ${section.style.shadow / 2}px ${section.style.shadow}px rgb(0 0 0 / .12)`
                  : undefined,
              }}
              onClick={(event) => {
                if (editing && !preview && event.target === event.currentTarget)
                  select({ sectionId: section.id, kind: "section" });
              }}
            >
              {editing && !preview && (
                <button
                  className="section-handle"
                  draggable
                  onDragStart={(e) =>
                    e.dataTransfer.setData("application/x-section", section.id)
                  }
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) =>
                    useEditor
                      .getState()
                      .reorderSections(
                        e.dataTransfer.getData("application/x-section"),
                        section.id,
                      )
                  }
                  onClick={() =>
                    select({ sectionId: section.id, kind: "section" })
                  }
                >
                  <GripVertical /> {section.type.replace("-", " ")}
                </button>
              )}
              <Component section={section} />
            </section>
          );
        })}
      </main>
      <Footer />
      {editing && !preview && (
        <>
          <ContextPanel />
          {themeOpen && <ThemePanel onClose={() => setThemeOpen(false)} />}
          <EditorAddButton onClick={() => setAddOpen(!addOpen)} />
          {addOpen && <AddMenu onClose={() => setAddOpen(false)} />}
        </>
      )}
      <FloatingToolbar onReset={() => setResetOpen(true)} />
      {editing && !onboardingSeen && <Onboarding />}
      {resetOpen && (
        <ConfirmReset
          onCancel={() => setResetOpen(false)}
          onConfirm={() => {
            reset();
            setResetOpen(false);
            fetch("/api/session", { method: "DELETE" }).catch(() => undefined);
          }}
        />
      )}
      {toast && (
        <div className="toast">
          <span>✓</span>
          {toast}
        </div>
      )}
      {!editing && (
        <button className="floating-reset" onClick={() => setResetOpen(true)}>
          <RotateCcw /> Reset Demo
        </button>
      )}
      {editing && !preview && (
        <button className="floating-theme" onClick={() => setThemeOpen(true)}>
          <Palette /> Theme
        </button>
      )}
      <a className="scroll-top" href="#top" aria-label="Back to top">
        <ChevronDown />
      </a>
    </div>
  );
}
